import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import { 
  ArrowRight, CheckCircle, TrendingUp, TrendingDown,
  Shield, Target, Bell, Brain, Zap, Lock, Eye,
  ChevronRight, Sparkles, Cpu, Activity, LineChart, BarChart3
} from 'lucide-react';

// FAQ for SEO
const faqData = [
  {
    question: "AI kripto sinyalleri nedir?",
    answer: "AI kripto sinyalleri, yapay zeka ve teknik analiz algoritmalarının birlikte çalışmasıyla üretilen trading fırsat bildirimleridir. Borsamex'in Sniper Engine'i 200'den fazla teknik göstergeyi analiz ederek potansiyel giriş noktalarını tespit eder."
  },
  {
    question: "Yapay zeka kripto sinyallerinde nasıl kullanılıyor?",
    answer: "AI (GPT-4o) piyasa durumunu yorumlar ve açıklama üretir. Asıl kararları deterministik Sniper Engine algoritması verir. Bu mimari sayede tutarlılık ve güvenilirlik sağlanır."
  },
  {
    question: "AI sinyalleri güvenilir mi?",
    answer: "Hiçbir sinyal sistemi %100 doğruluk garantisi veremez. Borsamex şeffaf performans verileri sunar ve her kullanıcının kendi risk yönetimini yapmasını önerir. Geçmiş performans gelecek sonuçları garanti etmez."
  },
  {
    question: "AI sinyal sistemi nasıl çalışır?",
    answer: "Sistem 7/24 piyasayı tarar, teknik göstergeler hesaplanır (RSI, MACD, Bollinger vb.), Sniper Engine skorlama yapar ve minimum kalite skorunu geçen setuplar sinyal olarak yayınlanır."
  },
  {
    question: "Borsamex borsama bağlanır mı?",
    answer: "Hayır. Borsamex sadece sinyal ve analiz sağlar. Borsanıza bağlanmıyoruz, API key istemiyoruz, fonlarınıza erişimimiz yok. Tüm işlemleri kendi hesabınızda kendiniz yaparsınız."
  }
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": { "@type": "Answer", "text": item.answer }
  }))
};

const AiKriptoSinyalleriPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="AI Kripto Sinyalleri – Yapay Zeka Destekli Trading | Borsamex"
        description="AI kripto sinyalleri ile trading fırsatlarını yakalayın. Sniper Engine 7/24 piyasayı tarar, teknik analiz yapar ve risk odaklı sinyaller üretir. Ücretsiz deneyin."
        keywords="ai kripto sinyalleri, yapay zeka kripto sinyalleri, ai trading sinyalleri, yapay zeka trading, kripto sinyal botu, otomatik kripto analizi"
        canonical="https://borsamex.com/ai-kripto-sinyalleri"
        structuredData={structuredData}
      />

      {/* HERO */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-transparent to-brand-green/5" />
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="mb-6 px-4 py-2 bg-purple-500/10 text-purple-400 border-purple-500/20">
              <Brain className="h-4 w-4 mr-2" />
              GPT-4o + Sniper Engine
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
              AI Kripto Sinyalleri<br />
              <span className="text-brand-green">Yapay Zeka Destekli Trading</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Borsamex'in <strong className="text-white">yapay zeka destekli sinyal motoru</strong>, 
              200+ teknik göstergeyi analiz ederek kripto piyasasında potansiyel fırsatları tespit eder. 
              Sistem <strong className="text-brand-green">7/24</strong> çalışır.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                  Ücretsiz Dene <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/nasil-calisir">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  Nasıl Çalışır?
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Nedir Bölümü */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              AI Kripto Sinyalleri Nedir?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Yapay zeka ve teknik analizin birleştiği nokta
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Cpu className="h-5 w-5 text-purple-400" />
                  Sniper Engine Algoritması
                </h3>
                <p className="text-slate-400">
                  Deterministik kurallarla çalışan çekirdek algoritma. RSI, MACD, Bollinger Bands, ATR gibi 
                  200'den fazla teknik göstergeyi analiz eder. Tutarlı ve öngörülebilir sonuçlar üretir.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Brain className="h-5 w-5 text-cyan-400" />
                  AI Yorum Katmanı
                </h3>
                <p className="text-slate-400">
                  GPT-4o tabanlı AI sistemi <strong className="text-white">karar vermez</strong> – sadece piyasa durumunu yorumlar. 
                  Kararları algoritma verir, AI açıklama ve bağlam sağlar.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-brand-green" />
                  Risk Odaklı Yaklaşım
                </h3>
                <p className="text-slate-400">
                  Her sinyalde ATR bazlı TP ve Stop seviyeleri. 3 kademeli kar alma stratejisi. 
                  Risk yönetimi sistemin merkezinde.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-slate-900/80">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                      <Brain className="h-8 w-8 text-purple-400" />
                    </div>
                    <h4 className="text-lg font-bold text-white">AI + Algoritma Mimarisi</h4>
                    <p className="text-sm text-slate-400">Güvenilir ve tutarlı sinyal üretimi</p>
                  </div>
                  
                  <div className="space-y-3">
                    {[
                      { label: "Teknik Analiz", value: "200+ Gösterge", color: "text-blue-400" },
                      { label: "Sniper Engine", value: "Deterministik", color: "text-amber-400" },
                      { label: "AI Yorumu", value: "GPT-4o", color: "text-purple-400" },
                      { label: "Sinyal Kalitesi", value: "Skorlama Sistemi", color: "text-brand-green" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                        <span className="text-slate-400 text-sm">{item.label}</span>
                        <span className={`font-medium ${item.color}`}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Demo Sinyal Kartı - Gerçek UI Temsili */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              AI Sinyal Kartı Nasıl Görünür?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Sistem bir fırsat tespit ettiğinde bu formatta sinyal üretir
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Card className="border-green-500/30 bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden">
                <CardContent className="p-0">
                  {/* Header */}
                  <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <span className="text-lg">₿</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white">BTC-USDT</span>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">LONG</Badge>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <span>Güç:</span>
                          {[1,2,3,4].map(i => <span key={i} className="text-amber-400">★</span>)}
                          <span className="text-slate-600">★</span>
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs">
                      <Brain className="h-3 w-3 mr-1" />AI
                    </Badge>
                  </div>
                  
                  {/* Body */}
                  <div className="p-4 space-y-4">
                    {/* Entry */}
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">GİRİŞ FİYATI</p>
                      <p className="font-mono text-xl text-white font-bold">$97,450</p>
                    </div>
                    
                    {/* TP Levels */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-green-900/20 rounded border border-green-500/20">
                        <span className="text-xs text-green-400">TP1</span>
                        <span className="font-mono text-green-400 text-sm">$99,200</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-emerald-900/20 rounded border border-emerald-500/20">
                        <span className="text-xs text-emerald-400">TP2</span>
                        <span className="font-mono text-emerald-400 text-sm">$101,800</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-cyan-900/20 rounded border border-cyan-500/20">
                        <span className="text-xs text-cyan-400">TP3</span>
                        <span className="font-mono text-cyan-400 text-sm">$105,500</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-red-900/20 rounded border border-red-500/20">
                        <span className="text-xs text-red-400">STOP</span>
                        <span className="font-mono text-red-400 text-sm">$95,100</span>
                      </div>
                    </div>
                    
                    {/* AI Commentary */}
                    <div className="p-3 bg-purple-900/10 rounded-lg border border-purple-500/20">
                      <p className="text-xs text-purple-400 mb-1 flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> Sistem Yorumu
                      </p>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        Yükseliş momentumu güçleniyor. Destek seviyesi korunuyor, hacim artışı pozitif.
                      </p>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="px-4 py-3 bg-slate-800/30 border-t border-slate-700/50">
                    <p className="text-xs text-slate-500 text-center">Örnek sinyal kartı • Gerçek zamanlı veriler değişiklik gösterir</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Özellikler */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              AI Sinyal Sisteminin Özellikleri
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Activity className="h-7 w-7" />, title: "7/24 Tarama", desc: "Piyasa sürekli izlenir", color: "bg-blue-500/20 text-blue-400" },
              { icon: <LineChart className="h-7 w-7" />, title: "Teknik Analiz", desc: "200+ gösterge analizi", color: "bg-amber-500/20 text-amber-400" },
              { icon: <Bell className="h-7 w-7" />, title: "Anlık Bildirim", desc: "Web + Telegram", color: "bg-purple-500/20 text-purple-400" },
              { icon: <Target className="h-7 w-7" />, title: "Risk Yönetimi", desc: "TP/SL seviyeleri", color: "bg-brand-green/20 text-brand-green" }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} viewport={{ once: true }}>
                <Card className="border-slate-700/50 bg-slate-900/50 h-full text-center">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-4`}>{item.icon}</div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sinyal Türleri - Internal Linking Hub */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Sinyal Türleri
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Piyasa koşullarına göre farklı türde sinyaller üretilir
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* BTC Sinyalleri */}
            <Link to="/btc-sinyalleri" className="group">
              <Card className="border-amber-500/20 bg-slate-900/50 h-full hover:border-amber-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <span className="text-xl">₿</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">BTC Sinyalleri</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Bitcoin özelinde LONG ve SHORT fırsatları.
                  </p>
                  <span className="text-amber-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Detaylar <ChevronRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            {/* ETH Sinyalleri */}
            <Link to="/eth-sinyalleri" className="group">
              <Card className="border-blue-500/20 bg-slate-900/50 h-full hover:border-blue-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <span className="text-xl text-blue-400">Ξ</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">ETH Sinyalleri</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Ethereum özelinde LONG ve SHORT fırsatları.
                  </p>
                  <span className="text-blue-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Detaylar <ChevronRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            {/* LONG Sinyaller */}
            <Link to="/long-sinyaller" className="group">
              <Card className="border-green-500/20 bg-slate-900/50 h-full hover:border-green-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <TrendingUp className="h-6 w-6 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-green-400 transition-colors">LONG Sinyaller</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Yükseliş yönlü pozisyonlar.
                  </p>
                  <span className="text-green-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Detaylar <ChevronRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            {/* SHORT Sinyaller */}
            <Link to="/short-sinyaller" className="group">
              <Card className="border-red-500/20 bg-slate-900/50 h-full hover:border-red-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <TrendingDown className="h-6 w-6 text-red-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-red-400 transition-colors">SHORT Sinyaller</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Düşüş yönlü pozisyonlar.
                  </p>
                  <span className="text-red-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Detaylar <ChevronRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Rehber Bölümü - Eğitim Linki */}
      <section className="py-16 bg-gradient-to-b from-transparent to-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Yeni Misin? Temel Kavramları Öğren
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Sinyal okumayı öğrenmek için ücretsiz rehberlerimize göz at
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/rehber/kripto-sinyali-nedir" className="group">
              <Card className="border-purple-500/20 bg-slate-900/50 h-full hover:border-purple-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">Kripto Sinyali Nedir?</h3>
                  <p className="text-slate-400 text-sm">TP, Stop Loss ve temel kavramlar</p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/rehber/kripto-sinyali-nasil-okunur" className="group">
              <Card className="border-amber-500/20 bg-slate-900/50 h-full hover:border-amber-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">Sinyal Nasıl Okunur?</h3>
                  <p className="text-slate-400 text-sm">Pratik sinyal kartı okuma rehberi</p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="group">
              <Card className="border-red-500/20 bg-slate-900/50 h-full hover:border-red-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-red-400 transition-colors">Risk Yönetimi</h3>
                  <p className="text-slate-400 text-sm">Sermaye koruma stratejileri</p>
                </CardContent>
              </Card>
            </Link>
          </div>

          <div className="text-center mt-8">
            <Link to="/rehber">
              <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Tüm Rehberleri Gör →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Ne Değildir */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              AI Kripto Sinyalleri Ne Değildir?
            </h2>
            <p className="text-slate-400">Şeffaflık ve güven için net bilgilendirme</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-red-500/20 bg-red-950/10">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-red-400 mb-4">❌ Bunlar Değiliz</h3>
                <ul className="space-y-3 text-sm text-slate-300">
                  <li className="flex items-start gap-2"><span className="text-red-500 shrink-0">✗</span>Garantili kazanç vaat eden sistem</li>
                  <li className="flex items-start gap-2"><span className="text-red-500 shrink-0">✗</span>Borsanıza bağlanan bot</li>
                  <li className="flex items-start gap-2"><span className="text-red-500 shrink-0">✗</span>Otomatik işlem yapan platform</li>
                  <li className="flex items-start gap-2"><span className="text-red-500 shrink-0">✗</span>Yatırım danışmanlığı hizmeti</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-green-500/20 bg-green-950/10">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-green-400 mb-4">✅ Bunları Sunuyoruz</h3>
                <ul className="space-y-3 text-sm text-slate-300">
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Şeffaf performans verileri</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Algoritmik sinyal ve analiz</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Risk odaklı TP/SL seviyeleri</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Karar destek aracı</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Sıkça Sorulan Sorular</h2>
          </div>

          <div className="space-y-4">
            {faqData.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} viewport={{ once: true }}>
                <details className="group">
                  <summary className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                    <span className="font-medium text-white pr-4">{item.question}</span>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-open:rotate-90 transition-transform shrink-0" />
                  </summary>
                  <div className="px-4 py-3 text-sm text-slate-400 leading-relaxed">{item.answer}</div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-t from-purple-500/10 to-transparent">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              AI Kripto Sinyallerini Dene
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Ücretsiz kayıt ol, sinyalleri takip et, kendi kararını ver.
            </p>
            
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-10 py-6 text-lg rounded-xl shadow-lg shadow-brand-green/25">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <p className="mt-8 text-xs text-slate-600 max-w-xl mx-auto">
              Borsamex sistem çıktıları yatırım tavsiyesi niteliği taşımaz. Kripto para ticareti yüksek risk içerir.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AiKriptoSinyalleriPage;
